import { useEffect, useMemo, useState } from "react";
import { Filter, X } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import DressCard from "@/components/DressCard";
import { api } from "@/lib/api";

export default function Catalog() {
  const [dresses, setDresses] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [availability, setAvailability] = useState("all"); // all | available | reserved
  const [sizes, setSizes] = useState([]);
  const [colors, setColors] = useState([]);
  const [priceMax, setPriceMax] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/dresses")
      .then((r) => setDresses(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const allSizes = useMemo(() => Array.from(new Set(dresses.map((d) => d.size).filter(Boolean))), [dresses]);
  const allColors = useMemo(() => Array.from(new Set(dresses.map((d) => d.color).filter(Boolean))), [dresses]);
  const maxPrice = useMemo(() => Math.max(0, ...dresses.map((d) => d.price)), [dresses]);

  useEffect(() => {
    if (priceMax === null && maxPrice > 0) setPriceMax(maxPrice);
  }, [maxPrice, priceMax]);

  const toggle = (arr, val, setter) =>
    setter(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);

  const filtered = useMemo(() => {
    return dresses.filter((d) => {
      if (availability === "available" && !d.available) return false;
      if (availability === "reserved" && d.available) return false;
      if (sizes.length && !sizes.includes(d.size)) return false;
      if (colors.length && !colors.includes(d.color)) return false;
      if (priceMax !== null && d.price > priceMax) return false;
      return true;
    });
  }, [dresses, availability, sizes, colors, priceMax]);

  const resetFilters = () => {
    setAvailability("all");
    setSizes([]);
    setColors([]);
    setPriceMax(maxPrice);
  };

  const activeFilterCount =
    (availability !== "all" ? 1 : 0) +
    (sizes.length ? 1 : 0) +
    (colors.length ? 1 : 0) +
    (priceMax !== null && priceMax < maxPrice ? 1 : 0);

  return (
    <div data-testid="catalog-page" className="bg-cream min-h-screen">
      <Navbar />
      <section className="pt-32 md:pt-40 pb-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="overline mb-4"><span className="gold-line" />Die Kollektion</div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
            Alle Kleider
          </h1>
          <p className="mt-6 text-ink/70 max-w-xl text-lg font-light">
            Handverlesen, gepflegt und bereit für deinen Auftritt. Wähle ein Kleid und sende
            uns eine Anfrage – wir reservieren es für deine Anprobe.
          </p>

          <div className="mt-10 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest">
              <button
                data-testid="filter-all"
                onClick={() => setAvailability("all")}
                className={`px-4 py-2 border transition-colors ${availability === "all" ? "border-ink bg-ink text-cream" : "border-[#E5E0D8] text-ink/70 hover:border-ink"}`}
              >
                Alle ({dresses.length})
              </button>
              <button
                data-testid="filter-available"
                onClick={() => setAvailability("available")}
                className={`px-4 py-2 border transition-colors ${availability === "available" ? "border-ink bg-ink text-cream" : "border-[#E5E0D8] text-ink/70 hover:border-ink"}`}
              >
                Verfügbar
              </button>
              <button
                data-testid="filter-reserved"
                onClick={() => setAvailability("reserved")}
                className={`px-4 py-2 border transition-colors ${availability === "reserved" ? "border-ink bg-ink text-cream" : "border-[#E5E0D8] text-ink/70 hover:border-ink"}`}
              >
                Reserviert
              </button>
            </div>
            <button
              data-testid="toggle-filters"
              onClick={() => setShowFilters((v) => !v)}
              className="inline-flex items-center gap-2 px-4 py-2 border border-ink text-xs uppercase tracking-widest hover:bg-ink hover:text-cream transition-colors"
            >
              <Filter size={14} /> Filter
              {activeFilterCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] bg-gold text-cream text-[10px]">{activeFilterCount}</span>
              )}
            </button>
          </div>

          {showFilters && (
            <div data-testid="filter-panel" className="mt-6 p-6 md:p-8 bg-white border border-[#E5E0D8] grid md:grid-cols-3 gap-8">
              <FilterGroup label="Größe">
                <div className="flex flex-wrap gap-2">
                  {allSizes.map((s) => (
                    <button
                      key={s}
                      data-testid={`filter-size-${s}`}
                      onClick={() => toggle(sizes, s, setSizes)}
                      className={`px-3 py-1.5 text-xs uppercase tracking-widest border transition-colors ${sizes.includes(s) ? "border-ink bg-ink text-cream" : "border-[#E5E0D8] hover:border-ink"}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </FilterGroup>
              <FilterGroup label="Farbe">
                <div className="flex flex-wrap gap-2">
                  {allColors.map((c) => (
                    <button
                      key={c}
                      data-testid={`filter-color-${c}`}
                      onClick={() => toggle(colors, c, setColors)}
                      className={`px-3 py-1.5 text-xs uppercase tracking-widest border transition-colors ${colors.includes(c) ? "border-ink bg-ink text-cream" : "border-[#E5E0D8] hover:border-ink"}`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </FilterGroup>
              <FilterGroup label={`Preis bis ${priceMax ?? 0} €`}>
                <input
                  data-testid="filter-price"
                  type="range"
                  min={0}
                  max={maxPrice}
                  step={5}
                  value={priceMax ?? maxPrice}
                  onChange={(e) => setPriceMax(Number(e.target.value))}
                  className="w-full accent-[#C5A059]"
                />
              </FilterGroup>
              {activeFilterCount > 0 && (
                <div className="md:col-span-3 flex justify-end">
                  <button
                    data-testid="reset-filters"
                    onClick={resetFilters}
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-ink/70 hover:text-ink"
                  >
                    <X size={14} /> Filter zurücksetzen
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <section className="pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="mb-6 text-sm text-ink/60">{loading ? "Lädt…" : `${filtered.length} Kleider`}</div>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[3/4] bg-stone-100" />
                  <div className="h-5 bg-stone-100 mt-4 w-3/4" />
                  <div className="h-3 bg-stone-100 mt-2 w-1/2" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
              {filtered.map((d, i) => (
                <DressCard key={d.id} dress={d} whatsappNumber={settings?.whatsapp_number} index={i} />
              ))}
            </div>
          )}
          {!loading && dresses.length === 0 ? (
            <div data-testid="catalog-empty" className="py-24 md:py-32 text-center border border-dashed border-[#E5E0D8] bg-white">
              <div className="overline mb-4 text-gold">In Vorbereitung</div>
              <h3 className="font-display text-3xl md:text-4xl mb-3">Aktuell sind keine Kleider verfügbar.</h3>
              <p className="text-ink/65 max-w-md mx-auto">
                Unsere Kollektion wird gerade vorbereitet. Bald findest du hier neue Kleider.
                Schreib uns gerne über WhatsApp – wir beraten dich auch persönlich.
              </p>
            </div>
          ) : !loading && filtered.length === 0 ? (
            <div className="py-24 text-center text-ink/60">
              Keine Kleider entsprechen deinen Filtern.
              <div className="mt-4">
                <button onClick={resetFilters} className="text-sm uppercase tracking-widest link-underline">
                  Filter zurücksetzen
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function FilterGroup({ label, children }) {
  return (
    <div>
      <div className="overline mb-3">{label}</div>
      {children}
    </div>
  );
}
